import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ElementRef, Renderer2, HostListener, ChangeDetectorRef } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { selectBoxHide, selectBoxInput, selectBoxOption } from '../../utilities/selectBox.utilities';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, ValidatorFn } from '@angular/forms';
import { buildFieldValidators, getFieldErrorMessage, getMaxLengthFromRules, getMinLengthFromRules } from '../../utilities/form-validators.utility';
import { CareerForm, CareerFormField } from '../../models/career.interface';
import { CommonApiService } from '../../services/commonapi.service';
import { CommonHelperService } from '../../services/commonhelper.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { InputFocusDirective } from '../../directives/input.focus.directive';
import { InputMaskDirective } from '../../directives/inputMask.directive';
import { RestrictCopyPasteDirective } from '../../directives/RestrictCopyPaste.directive';
import { CongratulationPopupComponent } from '../congratulation-popup/congratulation-popup.component';
import { ModalService } from '../../services/modal.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-dynamic-form',
    imports: [ReactiveFormsModule, NgOptimizedImage, NgScrollbarModule, InputFocusDirective, InputMaskDirective, RestrictCopyPasteDirective, CongratulationPopupComponent],
    templateUrl: './dynamic-form.component.html',
    styleUrls: ['./dynamic-form.component.scss']
})
export class DynamicFormComponent implements OnInit, OnChanges {
    @Input() formConfig!: CareerForm;
    @Input() submitButtonText = 'Get in Touch';
    @Input() submitButtonClass = 'button button-black button-arrow button-rounded';
    @Input() submittingButtonText = 'Submitting...';
    @Input() extraFields: { [key: string]: string } = {};
    @Output() formSubmitted = new EventEmitter<boolean>();

    readonly assetsCDNUrl = environment.assetsCDNUrl;
    dynamicForm!: FormGroup;
    fields: CareerFormField[] = [];
    isSubmitted = false;
    isSubmitting = false;
    errorMessage = '';
    apiFieldErrors: { [key: string]: string } = {};

    // Captcha
    queryCaptcha: SafeUrl | null = null;
    private captchaToken = '';

    private validationMap: { [key: string]: { validators: ValidatorFn[]; messages: { [key: string]: string } } } = {};

    successMessage = '';

    constructor(
        private readonly fb: FormBuilder,
        private readonly commonApiService: CommonApiService,
        private readonly commonHelperService: CommonHelperService,
        private readonly sanitizer: DomSanitizer,
        private readonly el: ElementRef,
        private readonly renderer: Renderer2,
        private readonly modalService: ModalService,
        private readonly cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.buildForm();
        if (typeof window !== 'undefined') {
            this.generateCaptcha();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['formConfig'] && this.formConfig) {
            this.buildForm();
        }
    }

    private buildForm(): void {
        if (!this.formConfig?.fields) return;
        this.fields = this.formConfig.fields.map(f => ({
            ...f,
            type: (f.type === 'dropdown' ? 'select' : f.type) as CareerFormField['type']
        }));

        const formControls: { [key: string]: any } = {};
        this.fields.forEach(field => {
            const dynamic = this.buildDynamicValidators(field);
            this.validationMap[field.name] = dynamic;
            const validators: ValidatorFn[] = [...dynamic.validators];
            if (field.required) validators.push(Validators.required);
            formControls[field.name] = ['', validators];
        });

        formControls['captcha_form'] = ['', [Validators.required, Validators.minLength(6)]];
        this.validationMap['captcha_form'] = {
            validators: [],
            messages: { required: 'Please enter captcha', minlength: 'Please enter a valid captcha' }
        };

        this.dynamicForm = this.fb.group(formControls);

        Object.keys(formControls).forEach(key => {
            this.dynamicForm.get(key)?.valueChanges.subscribe(() => {
                if (this.apiFieldErrors[key]) delete this.apiFieldErrors[key];
            });
        });
    }

    async generateCaptcha(): Promise<void> {
        try {
            const response = await firstValueFrom(this.commonApiService.generateFormCaptcha(this.formConfig.form_id));
            this.captchaToken = response?.data?.token || response?.data?.captchaToken || '';
            const captchaValue = response?.data?.captcha_image || response?.data?.captchaValue || null;
            if (captchaValue) {
                const blobUrl = this.commonHelperService.getBlob(captchaValue);
                // Safe: blobUrl is a browser-generated blob: URI from URL.createObjectURL(), not user input
                this.queryCaptcha = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
            }
            this.cdr.markForCheck();
        } catch (e) {
            console.error('Captcha generation failed:', e);
        }
    }

    private buildDynamicValidators(field: CareerFormField): { validators: ValidatorFn[]; messages: { [key: string]: string } } {
        return buildFieldValidators(field);
    }

    getFieldError(fieldName: string): string | null {
        if (this.apiFieldErrors[fieldName]) return this.apiFieldErrors[fieldName];
        const control = this.dynamicForm?.get(fieldName);
        if (!control?.errors || !control?.touched) return null;
        const messages = this.validationMap[fieldName]?.messages || {};
        return getFieldErrorMessage(control.errors, messages, this.getLabel(fieldName));
    }

    private getLabel(fieldName: string): string {
        return this.fields.find(f => f.name === fieldName)?.label || fieldName;
    }

    submitForm(): void {
        this.dynamicForm.markAllAsTouched();
        if (this.dynamicForm?.invalid || this.isSubmitting) return;

        for (const key of Object.keys(this.dynamicForm.controls)) {
            if (this.dynamicForm.controls[key].invalid) {
                const invalidControl = this.el.nativeElement.querySelector('[formcontrolname="' + key + '"]');
                invalidControl?.focus();
                break;
            }
        }

        this.isSubmitting = true;
        this.errorMessage = '';
        this.apiFieldErrors = {};
        this.submitWithCaptcha();
    }

    private async submitWithCaptcha(): Promise<void> {
        const captchaValid = await this.validateCaptcha();
        if (!captchaValid) {
            this.isSubmitting = false;
            return;
        }

        const fields: { [key: string]: string } = {};

        // SECURITY FIX (VAPT): Convert file to base64 string before submitting.
        const filePromises = this.fields
            .filter(f => f.type === 'media')
            .map(field => {
                const file: File = this.dynamicForm.get(field.name)?.value;
                if (!file || !(file instanceof File)) return Promise.resolve();
                return new Promise<void>(resolve => {
                    const reader = new FileReader();
                    reader.onload = () => { fields[field.name] = reader.result as string; resolve(); };
                    reader.onerror = () => resolve();
                    reader.readAsDataURL(file);
                });
            });

        await Promise.all(filePromises);

        // Collect all non-file field values into the payload.
        this.fields.filter(f => f.type !== 'media').forEach(field => {
            const value = this.dynamicForm.get(field.name)?.value;
            if (value !== null && value !== undefined) {
                fields[field.name] = String(value);
            }
        });
        Object.assign(fields, this.extraFields);

        const payload = { form_id: this.formConfig.form_id, fields };
        this.commonApiService.submitForm(payload).subscribe({
            next: (response: any) => {
                if (response?.status === 200 || response?.success) {
                    this.successMessage = response?.message || 'The submission was successful.';
                    this.dynamicForm.reset();
                    this.clearSelectInputs();
                    this.removeFocus();
                    this.modalService.showModal('congrts');
                    this.formSubmitted.emit(true);
                    this.generateCaptcha();
                } else {
                    this.errorMessage = response?.message || 'Something went wrong.';
                    this.dynamicForm.patchValue({ captcha_form: '' });
                    this.generateCaptcha();
                }
                this.isSubmitting = false;
            },
            error: (err) => {
                const errBody = err?.error;
                if (errBody?.errors && typeof errBody.errors === 'object') {
                    this.apiFieldErrors = Object.fromEntries(
                        Object.entries(errBody.errors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : String(v)])
                    );
                    this.errorMessage = errBody.message || 'Please fix the errors below.';
                } else {
                    this.errorMessage = errBody?.message || err?.statusText || 'Something went wrong.';
                }
                this.isSubmitting = false;
                this.dynamicForm.patchValue({ captcha_form: '' });
                this.generateCaptcha();
            }
        });
    }

    private async validateCaptcha(): Promise<boolean> {
        try {
            const captchaValue = this.dynamicForm.get('captcha_form')?.value;
            const response = await firstValueFrom(this.commonApiService.verifyFormCaptcha(this.formConfig.form_id, this.captchaToken, captchaValue));
            const encoded = response?.data?.token;
            const xRandomToken = response?._xRandomToken;
            const parts = encoded ? atob(encoded).split('.') : [];

            if (response?.success && parts[0]?.includes('200') && parts[1] === xRandomToken) return true;
            this.errorMessage = parts[1] !== xRandomToken ? 'Invalid captcha token.' : (response?.message || 'Invalid captcha.');
            this.dynamicForm.patchValue({ captcha_form: '' });
            this.generateCaptcha();
            return false;
        } catch (e) {
            console.error('Captcha verification failed:', e);
            this.errorMessage = 'Captcha verification failed.';
            this.dynamicForm.patchValue({ captcha_form: '' });
            this.generateCaptcha();
            return false;
        }
    }

    private removeFocus(): void {
        if (typeof window !== 'undefined') {
            const elements = this.el.nativeElement.querySelectorAll('.form-group');
            elements.forEach((element: Element) => this.renderer.removeClass(element, 'focused'));
        }
    }

    private clearSelectInputs(): void {
        const selectInputs = this.el.nativeElement.querySelectorAll('.type-select .form-control');
        selectInputs.forEach((input: HTMLInputElement) => { input.value = ''; });
    }

    resetForm(): void {
        this.isSubmitted = false;
        this.errorMessage = '';
    }

    getMaxLength(field: CareerFormField): number {
        return getMaxLengthFromRules(field.validation?.rules || [], field.type);
    }

    getMinLength(field: CareerFormField): number | null {
        return getMinLengthFromRules(field.validation?.rules || [], field.type);
    }

    handler__selectInput($event: any) { selectBoxInput($event); }
    handler__selectBox($event: any) { $event.stopImmediatePropagation(); }
    handler__selectOption($event: any, fieldName: string, optValue: string) {
        this.dynamicForm.patchValue({ [fieldName]: optValue });
        this.dynamicForm.get(fieldName)?.markAsTouched();
        selectBoxOption($event);
    }

    @HostListener('document:click')
    onDocumentClick() { selectBoxHide('show'); }
}
